import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { sidebarItems } from "@/config/sidebar";
import { hasAnyOfRoles, hasAnyOfDepartments, hasAnyOfAgentIds } from "@/constants/appRoles";
import { useAppData } from "@/context/AppDataContext";
import { useAppRoles } from "@/services/AuthService";
import { useMsal } from "@azure/msal-react";
import { LogOut } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { instance } = useMsal();
  const location = useLocation();
  const appRoles = useAppRoles();
  const { currentUser } = useAppData();

  const visibleItems = useMemo(
    () =>
      sidebarItems.filter(
        (item) =>
          (!item.requiredRoles || hasAnyOfRoles(appRoles, item.requiredRoles)) &&
          (!item.requiredDepartments && !item.requiredAgentIds ||
            (item.requiredDepartments && hasAnyOfDepartments(currentUser?.departmentId, item.requiredDepartments)) ||
            (item.requiredAgentIds && hasAnyOfAgentIds(currentUser?.userId, item.requiredAgentIds)))
      ),
    [appRoles, currentUser]
  );

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  // The active item is the most specific (longest) URL that matches the path,
  // so nested routes like /dealer-direct-prop-queue don't also light up /underwriting.
  const activeUrl = useMemo(() => {
    const matches = visibleItems
      .map((item) => item.url)
      .filter((url) =>
        url === "/"
          ? location.pathname === "/"
          : location.pathname === url || location.pathname.startsWith(`${url}/`)
      );
    return matches.sort((a, b) => b.length - a.length)[0] ?? null;
  }, [visibleItems, location.pathname]);

  const isActive = (url: string) => url === activeUrl;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title} isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} tooltip="Sign Out">
              <LogOut />
              <span>Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
